import { cleanChar775, getPopupContainer, IItem, isNullOrUndefined, isPromise } from '@bimser/core';
import { GetIconComponent } from '@bimser/icons';
import { GetProps, Select } from 'antd';
import { BaseSelectRef } from 'rc-select';
import React from 'react';
import Styles from './assets/comboboxStyles.scss';
import { IComboboxLoadingArgs, IComboboxProps, IComboboxSelectedChangeEventArgs } from './entities';
import { IRawOptionType } from './entities/IComboboxProps';

import classNames from 'classnames/bind';
const cx = classNames.bind(Styles);

type OptionProps = GetProps<typeof Select.Option>;

const BCCombobox = React.forwardRef(
	(props: IComboboxProps, ref: React.ForwardedRef<BaseSelectRef>) => {
		const [currentItem, setCurrentItem] = React.useState({
			...((props.options &&
				Array.isArray(props.options) &&
				props.options.find(i => i.value === props.value)) ||
				{})
		});
		const [options, setOptions] = React.useState(props.options);

		Styles;

		React.useEffect(() => {
			if (currentItem?.value !== props.value) {
				setCurrentItem({
					...((options &&
						Array.isArray(options) &&
						options.find(i => i.value === props.value)) ||
						{})
				});
			}
		}, [props.value]);

		React.useEffect(() => {
			if (Array.isArray(props.options)) {
				setOptions(props.options);
				setCurrentItem(props.options.find(item => item.value === props.value));
			} else {
				getInitialOptions(props);
			}
		}, [props.options]);

		const getInitialOptions = (props: IComboboxProps) => {
			if (props.isRunOnLoadOptionsStartup) {
				onLoadOptions(true);
			}
		};

		const selectedChanged = (value: any, option: any) => {
			if (props.onSelectedChange) {
				if (props.rawOptions) {
					props.onSelectedChange(value);
				} else if (props.keyTextValue) {
					props.options.forEach(item => {
						if (!isNullOrUndefined(value) && item.value === value) {
							setCurrentItem({ key: item.key, text: item.text, value: item.value });

							props.onSelectedChange({
								senderArgs: { value, option },
								data: { key: item.key, text: item.text, value: item.value }
							});
						} else if (isNullOrUndefined(value)) {
							props.onSelectedChange({
								senderArgs: { value, option },
								data: undefined
							});
						}
					});
				} else {
					if (options && Array.isArray(options)) {
						const item = options.find(f => f.value === value);
						if (item) {
							setCurrentItem({ key: item.key, text: item.text, value: item.value });
						}

						props.onSelectedChange({
							senderArgs: { value, option, externalData: item?.externalData },
							data: value
						});
					}
				}
			} else {
				if (options && Array.isArray(options)) {
					const item = options.find(f => f.value === value);
					setCurrentItem(item);
				}
			}
		};

		const renderIcon = (item: IItem) =>
			GetIconComponent(item.icon, {
				title: item.externalData?.iconTitle || undefined,
				className: item.externalData?.iconClassName || undefined
			});

		const getOptions = () => {
			if (props.rawOptions) {
				return undefined;
			}

			let _options: Array<IItem> = [];

			if (options?.length) {
				_options = [...options];
			}

			if (props.renderItem) {
				return _options.map((item, i) => props.renderItem(item, i));
			}

			return _options.map((item, i) => {
				const itemText = item.icon ? <span>{item.text}</span> : item.text;

				return (
					<Select.Option
						className={item.icon ? Styles.ComboboxSelectItemContent : ''}
						key={item.key}
						value={item.value}
						title={item.text}
						disabled={item.disabled}
					>
						{renderIcon(item)}
						{props.renderText?.(item) || itemText}
					</Select.Option>
				);
			});
		};

		const onLoadOptions = (isInitialLoader?: boolean) => {
			if (props.onLoadOptions && (!props.isRunOnLoadOptionsStartup || isInitialLoader)) {
				const response: Promise<IComboboxLoadingArgs> = props.onLoadOptions() as any;
				if (isPromise(response)) {
					onLoadOptionsPromise(response);
				} else {
					setItemsInState(props.options);
				}
			} else {
				setItemsInState(props.options);
			}
		};

		const onLoadOptionsPromise = (response: Promise<IComboboxLoadingArgs>) => {
			if (response?.then) {
				response.then(args => {
					if (
						args &&
						args.options &&
						Array.isArray(args.options) &&
						args.allow !== false
					) {
						setItemsInState(args.options);
					} else {
						setItemsInState(props.options);
					}
				});
			} else {
				setItemsInState([]);
			}
		};

		const onFilterOption = (inputValue: string, option: any) => {
			const title = option?.title?.toLocaleLowerCase();
			const searchValue = inputValue.toLocaleLowerCase();
			return cleanChar775(title)?.indexOf(cleanChar775(searchValue)) >= 0 || false;
		};

		const setItemsInState = (items: Array<IItem>) => {
			if (items && Array.isArray(items)) {
				const _items: Array<IItem> = [...items];
				const currentItem = _items.find(item => item.value === props.value);
				setOptions(_items);
				setCurrentItem(currentItem ? currentItem : null);
			}
		};

		const getValue = () => {
			if (props.rawOptions) {
				return props.value;
			}
			if (currentItem) {
				if (
					options &&
					Array.isArray(options) &&
					options.find(i => i.value === currentItem.value)
				) {
					return currentItem.value;
				}
				return props.rawOptions ? props.value ?? undefined : undefined;
			}

			return undefined;
		};

		const onFocus = () => {
			onLoadOptions();
			props.onFocus?.();
		};

		const comboBoxClassNames = cx({
			ComboboxArrow: true,
			fromMobile: props.fromMobile,
			[props.className]: true
		});

		return (
			<Select
				ref={ref}
				allowClear={props.allowClear && !props.readonly}
				showSearch={props.showSearch && !props.readonly}
				defaultValue={props.defaultValue}
				mode={props.mode}
				maxTagCount={props.maxTagCount}
				tokenSeparators={props.tokenSeparators}
				autoFocus={props.autoFocus}
				dropdownStyle={props.dropdownStyle}
				style={props.style}
				optionFilterProp="children"
				onChange={selectedChanged}
				filterOption={props.filterOption || onFilterOption}
				value={getValue()}
				className={comboBoxClassNames}
				onFocus={onFocus}
				size={props.size}
				loading={props.loading}
				onBlur={props.onBlur}
				tabIndex={props.tabIndex}
				getPopupContainer={props.getPopupContainer || getPopupContainer}
				disabled={props.disabled}
				onKeyDown={props.onKeyDown}
				dropdownRender={props.dropdownRender}
				defaultOpen={props.defaultOpen}
				placeholder={props.placeholder}
				open={props.readonly ? false : undefined}
				onSearch={props.onSearch}
				notFoundContent={props.notFoundContent}
				popupClassName={props.dropdownClassName}
				onDropdownVisibleChange={props.onDropdownVisibleChange}
				options={props.rawOptions}
				children={getOptions()}
				data-selenium-id={props.seleniumId}
				variant={props.variant}
			/>
		);
	}
);

export default BCCombobox;

const BCComboboxOption = (props: OptionProps) => <Select.Option {...props} />;

const BCComboboxOptionGroup = (props: { key: string; children?: React.ReactNode }) => (
	<Select.OptGroup
		children={props.children}
		key={props.key}
	/>
);

export {
	BCComboboxOption,
	BCComboboxOptionGroup,
	BaseSelectRef as BCComboBoxRef,
	IComboboxLoadingArgs,
	IComboboxProps,
	IComboboxSelectedChangeEventArgs,
	IRawOptionType,
	Select
};
